import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { formatTimeTo12Hour } from '../lib/utils';
import { formatDateOnlyInTimezone } from '../utils/timezone';

interface Listing {
  _id: string;
  name: string;
  title: string;
}

interface RentalWindow {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startDayOfMonth?: number;
  startDayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  intervalMinutes?: number;
  repeatDaysOfWeek?: number[];
  specificDate?: string;
  isRepeating: boolean;
  pricePerInterval: number;
  quantity: number;
}

const ListingWindowsPage: React.FC = () => {
  const { listingId } = useParams();
  const [windows, setWindows] = useState<RentalWindow[]>([]);
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [windowToDelete, setWindowToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      const [listingData, windowsData] = await Promise.all([
        api.get<{ listing: Listing }>(`/listings/${listingId}`),
        api.get<{ windows: RentalWindow[] }>(`/windows/listing/${listingId}`),
      ]);
      setListing(listingData.listing);
      setWindows(windowsData.windows);
    } catch {
      toast.error('Failed to load windows');
    } finally {
      setIsLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDeleteModal = (windowId: string) => {
    setWindowToDelete(windowId);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!windowToDelete) return;

    setIsDeleting(true);
    try {
      const response = await api.delete<{ message: string; hadRentals: boolean }>(
        `/windows/${windowToDelete}`
      );
      
      if (response.hadRentals) {
        toast.warning('Window deleted. Note: This listing has existing rentals that may be affected.');
      } else {
        toast.success('Window deleted successfully');
      }
      
      setDeleteModalOpen(false);
      setWindowToDelete(null);
      fetchData();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to delete window');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatWindowDetails = (window: RentalWindow): string => {
    const parts: string[] = [];

    if (window.windowType === 'monthly') {
      parts.push(`Day ${window.startDayOfMonth} of month`);
    } else if (window.windowType === 'weekly') {
      // Matches Date.getUTCDay() (0 = Sunday), since that is what availability
      // computation compares startDayOfWeek/repeatDaysOfWeek against.
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      parts.push(dayNames[window.startDayOfWeek || 0]);
    } else if (window.windowType === 'daily') {
      if (window.isRepeating && window.repeatDaysOfWeek && window.repeatDaysOfWeek.length > 0) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const days = window.repeatDaysOfWeek.map((d) => dayNames[d]).join(', ');
        parts.push(`Repeats: ${days}`);
      } else if (window.specificDate) {
        const date = new Date(window.specificDate);
        parts.push(`One-time: ${formatDateOnlyInTimezone(date, 'UTC')}`);
      }
    }

    if (window.startTime && window.endTime) {
      parts.push(`${formatTimeTo12Hour(window.startTime)} - ${formatTimeTo12Hour(window.endTime)}`);
    }

    if (window.intervalMinutes && window.intervalMinutes > 0) {
      parts.push(`${window.intervalMinutes} min intervals`);
    }

    return parts.join(' • ');
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Rental Windows: {listing?.title || listing?.name}
          </h1>
          <Link to="/my-listings" className="text-primary-600 hover:text-primary-700">
            ← Back to My Listings
          </Link>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Windows ({windows.length})
          </h2>
          <Link to={`/listings/${listingId}/windows/create`}>
            <Button>Create New Window</Button>
          </Link>
        </div>

        {windows.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600 mb-4">No rental windows yet.</p>
            <p className="text-sm text-gray-500 mb-6">
              Create windows to define when and how your listing can be rented.
            </p>
            <Link to={`/listings/${listingId}/windows/create`}>
              <Button>Create Your First Window</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {windows.map((window) => (
              <div key={window._id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {window.name || 'Unnamed Window'}
                        </h3>
                        <p className="text-sm text-gray-500 capitalize mt-1">
                          {window.windowType} Window
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="text-gray-700">
                        <span className="font-medium">Schedule:</span>{' '}
                        {formatWindowDetails(window)}
                      </div>
                      <div className="flex gap-6 text-gray-600">
                        <div>
                          <span className="font-medium">Price:</span> ${window.pricePerInterval}
                          {window.intervalMinutes && window.intervalMinutes > 0
                            ? ` per ${window.intervalMinutes} min`
                            : ' per window'}
                        </div>
                        <div>
                          <span className="font-medium">Quantity:</span> {window.quantity}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Link to={`/listings/${listingId}/windows/${window._id}/edit`}>
                      <Button size="sm">Edit</Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => openDeleteModal(window._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setWindowToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Rental Window?"
        message="Are you sure you want to delete this window? This action cannot be undone. If your listing has existing rentals, they may be affected."
        confirmText="Delete Window"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ListingWindowsPage;

